import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { teamPreferencesService } from '../../services/teamPreferences.service';
import { queryKeys } from '../../services/query/queryKeys';
import { SellerDetailsModal } from './SellerDetailsModal';

/**
 * Seller actions for the Team → Sellers tab:
 *  - Details: fetches the enriched lvl1 profile (get_lvl1_profile) and shows
 *    it in a simplified modal. The header hosts Report (report generator) and
 *    Brand-AI (stacked on top of the details dialog — see the details modal).
 *  - Remove: deletes the seller reference from the team `seller_ids` pref.
 */

export interface SellerRef {
  /** lvl1 id (numeric string), e.g. "69123". */
  id: string;
  /** Seller display name. */
  label: string;
}

/** Hook exposing the seller actions + their mounted modals. */
export const useSellerActions = ({ onRemoved }: { onRemoved?: (id: string) => void } = {}) => {
  const queryClient = useQueryClient();
  const [detailsSeller, setDetailsSeller] = useState<SellerRef | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = async (seller: SellerRef) => {
    setRemovingId(seller.id);
    setRemoveError(null);
    try {
      // DELETE = remove the `id§Name` reference from the team pref `seller_ids`.
      const current = await teamPreferencesService.getPreferences();
      const refs = (current.seller_ids ?? '')
        .split(',')
        .map((ref) => ref.trim())
        .filter(Boolean);
      const next = refs.filter((ref) => {
        const refId = ref.split('§')[0];
        return refId !== seller.id && ref.toLowerCase() !== seller.label.toLowerCase();
      });
      await teamPreferencesService.updatePreferences({ seller_ids: next.join(',') });
      queryClient.invalidateQueries({ queryKey: queryKeys.preferences.teamSellerIds });
      onRemoved?.(seller.id);
    } catch (error: any) {
      setRemoveError(error?.message || 'Could not remove the seller.');
    } finally {
      setRemovingId(null);
    }
  };

  const modals = (
    <>
      {detailsSeller && <SellerDetailsModal seller={detailsSeller} onClose={() => setDetailsSeller(null)} />}
    </>
  );

  return {
    openDetails: setDetailsSeller,
    remove,
    removingId,
    removeError,
    modals,
  };
};
